import React, { useState, useEffect, useRef } from 'react';
import { api } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, CheckCircle, Circle, Calendar as CalendarIcon, List, Trash2, RotateCcw, Edit } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import './TasksPage.css'; // Custom calendar styles
import { formatDate } from '../utils/dates';

// Helper functions for LOCAL datetime handling (NO timezone conversion)
const toLocalDatetimeString = (isoOrLocalString) => {
  if (!isoOrLocalString) return '';
  
  // If it's already a local string (no Z), return as-is
  if (!isoOrLocalString.includes('Z') && !isoOrLocalString.match(/[+-]\d{2}:\d{2}$/)) {
    return isoOrLocalString.slice(0, 16); // YYYY-MM-DDTHH:mm
  }
  
  // If it has a Z or timezone, convert from UTC to local
  const date = new Date(isoOrLocalString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const fromLocalDatetimeString = (localString) => {
  if (!localString) return null;
  
  // CRITICAL: Return the local datetime string AS-IS (no timezone, no Z)
  // Backend should store this as naive datetime
  // Format: YYYY-MM-DDTHH:mm:ss (no Z, no offset)
  const withSeconds = localString.includes(':00:00') || localString.split(':').length === 3
    ? localString 
    : `${localString}:00`;
  
  return withSeconds; // Return plain local string, NOT ISO with Z
};

const TasksPage = () => {
  const [tasks, setTasks] = useState([]);
  const [filter, setFilter] = useState(() => {
    return localStorage.getItem('task_filter_preference') || 'all';
  });
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('task_view_mode_preference') || 'calendar';
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const calendarRef = useRef(null);
  const [tooltip, setTooltip] = useState({ show: false, x: 0, y: 0, task: null });

  const [formData, setFormData] = useState({
    entity_type: 'General',
    entity_id: 'general',
    title: '',
    description: '',
    due_at: '',
    priority: 'Med',
  });

  useEffect(() => {
    fetchTasks();
  }, [filter, viewMode]);

  const fetchTasks = async () => {
    try {
      const params = {};
      if (viewMode === 'deleted') {
        params.include_deleted = true;
      } else if (filter !== 'all') {
        params.completed = filter === 'completed';
      }
      const response = await api.get('/tasks', { params });
      setTasks(response.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error('Failed to fetch tasks');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        due_at: fromLocalDatetimeString(formData.due_at), // Returns local string (no Z)
      };
      
      if (editingTask) {
        // Update existing task
        await api.put(`/tasks/${editingTask.id}`, payload);
        toast.success('Task updated successfully');
      } else {
        // Create new task
        await api.post('/tasks', payload);
        toast.success('Task created successfully');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchTasks();
    } catch (error) {
      console.error('Error saving task:', error);
      toast.error('Failed to save task');
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setFormData({
      entity_type: task.entity_type || 'General',
      entity_id: task.entity_id || 'general',
      title: task.title,
      description: task.description || '',
      due_at: toLocalDatetimeString(task.due_at),
      priority: task.priority || 'Med',
    });
    setDialogOpen(true);
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await api.post(`/tasks/${taskId}/complete`);
      toast.success('Task completed');
      fetchTasks();
    } catch (error) {
      console.error('Error completing task:', error);
      toast.error('Failed to complete task');
    }
  };

  const handleDelete = (task) => {
    setTaskToDelete(task);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/tasks/${taskToDelete.id}`);
      toast.success('Task deleted successfully');
      setDeletingTaskId(null);
      fetchTasks();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    }
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    localStorage.setItem('task_filter_preference', newFilter);
  };

  const handleRestore = async (task) => {
    try {
      await api.post(`/tasks/${task.id}/restore`);
      toast.success('Task restored successfully');
      fetchTasks();
    } catch (error) {
      console.error('Error restoring task:', error);
      toast.error('Failed to restore task');
    }
  };

  const handleDateClick = (arg) => {
    setFormData({ ...formData, due_at: arg.dateStr + 'T09:00' });
    setDialogOpen(true);
  };

  const handleEventDrop = async (info) => {
    try {
      // Convert dropped date to local datetime string (no Z)
      const droppedDate = info.event.start;
      const localDateTimeString = toLocalDatetimeString(droppedDate.toISOString());
      
      await api.put(`/tasks/${info.event.id}`, { due_at: localDateTimeString });
      toast.success('Task rescheduled');
      fetchTasks();
    } catch (error) {
      console.error('Error rescheduling task:', error);
      toast.error('Failed to reschedule task');
      info.revert();
    }
  };

  const resetForm = () => {
    setEditingTask(null);
    setFormData({
      entity_type: 'General',
      entity_id: 'general',
      title: '',
      description: '',
      due_at: '',
      priority: 'Med',
    });
  };

  const getPriorityColor = (priority) => {
    const colors = {
      High: 'bg-red-100 text-red-800',
      Med: 'bg-yellow-100 text-yellow-800',
      Low: 'bg-gray-100 text-gray-800',
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  const getEventColor = (priority) => {
    const colors = {
      High: '#ef4444',
      Med: '#f59e0b',
      Low: '#6b7280',
    };
    return colors[priority] || '#6b7280';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const pendingTasks = tasks.filter(t => !t.completed_at && !t.soft_delete);
  const completedTasks = tasks.filter(t => t.completed_at && !t.soft_delete);
  const deletedTasks = tasks.filter(t => t.soft_delete);
  const overdueTasks = pendingTasks.filter(t => {
    if (!t.due_at) return false;
    return new Date(t.due_at) < new Date();
  });

  // Convert calendar events - CRITICAL: Use local datetime strings (no timezone conversion)
  const calendarEvents = tasks
    .filter(t => !t.soft_delete && t.due_at)
    .map(task => {
      // Determine category based on entity_type or priority
      let category = 'general';
      if (task.entity_type === 'Lead' || task.entity_type === 'Contact' || task.entity_type === 'Company') {
        category = 'crm';
      } else if (task.title?.toLowerCase().includes('follow') || task.entity_type === 'Follow-up') {
        category = 'followup';
      } else if (task.entity_type === 'Task' || task.priority) {
        category = 'task';
      }
      
      // CRITICAL FIX: Pass datetime string directly to FullCalendar (no Date conversion)
      // If the backend returns "2025-10-28T10:30:00" (no Z), use it as-is
      // If it returns "2025-10-28T14:30:00.000Z" (UTC), strip Z and convert to local
      let eventStart = task.due_at;
      
      // If it has a Z (UTC), convert to local string
      if (task.due_at.includes('Z') || task.due_at.match(/[+-]\d{2}:\d{2}$/)) {
        eventStart = toLocalDatetimeString(task.due_at);
      }
      
      return {
        id: task.id,
        title: task.title,
        start: eventStart, // Pass local datetime string (no Z)
        allDay: false,
        backgroundColor: getEventColor(task.priority),
        borderColor: getEventColor(task.priority),
        extendedProps: {
          priority: task.priority,
          entity_type: task.entity_type,
          completed: !!task.completed_at,
          category: category,
          description: task.description,
        },
      };
    });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Calendar & Tasks</h1>
          <p className="text-slate-600 mt-1">Manage your tasks and schedule</p>
        </div>
        <div className="flex gap-3">
          {/* Pill-style view selector */}
          <div className="flex gap-1 bg-slate-100 p-1 rounded-full">
            <Button 
              variant="ghost"
              size="sm"
              className={`rounded-full px-4 ${viewMode === 'calendar' ? 'bg-[#247C75] text-white hover:bg-[#1f6b65] hover:text-white' : 'hover:bg-slate-200'}`}
              onClick={() => {
                setViewMode('calendar');
                localStorage.setItem('task_view_mode_preference', 'calendar');
              }}
            >
              <CalendarIcon className="h-4 w-4 mr-2" />
              Calendar
            </Button>
            <Button 
              variant="ghost"
              size="sm"
              className={`rounded-full px-4 ${viewMode === 'list' ? 'bg-[#247C75] text-white hover:bg-[#1f6b65] hover:text-white' : 'hover:bg-slate-200'}`}
              onClick={() => {
                setViewMode('list');
                localStorage.setItem('task_view_mode_preference', 'list');
              }}
            >
              <List className="h-4 w-4 mr-2" />
              List
            </Button>
            <Button 
              variant="ghost"
              size="sm"
              className={`rounded-full px-4 ${viewMode === 'deleted' ? 'bg-[#247C75] text-white hover:bg-[#1f6b65] hover:text-white' : 'hover:bg-slate-200'}`}
              onClick={() => {
                setViewMode('deleted');
                localStorage.setItem('task_view_mode_preference', 'deleted');
              }}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Deleted
              {deletedTasks.length > 0 && (
                <Badge className="ml-2 bg-red-500">{deletedTasks.length}</Badge>
              )}
            </Button>
          </div>
          <Button className="bg-[#247C75] hover:bg-[#1f6b65]" onClick={() => { resetForm(); setDialogOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            Add Task
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Pending Tasks</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{pendingTasks.length}</div>
            <p className="text-xs text-slate-500 mt-1">Active tasks</p>
          </CardContent>
        </Card>
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Overdue</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-600">{overdueTasks.length}</div>
            <p className="text-xs text-slate-500 mt-1">Need attention</p>
          </CardContent>
        </Card>
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Completed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">{completedTasks.length}</div>
            <p className="text-xs text-slate-500 mt-1">Tasks done</p>
          </CardContent>
        </Card>
      </div>

      {/* Calendar View with Sidebar */}
      {viewMode === 'calendar' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Calendar */}
          <div className="lg:col-span-2">
            <Card className="shadow-md">
              <CardContent className="pt-6">
                <FullCalendar
                  ref={calendarRef}
                  plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
                  initialView="dayGridMonth"
                  timeZone="local"
                  dayMaxEvents={3}
                  moreLinkClick="popover"
                  eventMaxStack={3}
                  eventDisplay="block"
                  displayEventTime={true}
                  displayEventEnd={false}
                  eventTimeFormat={{
                    hour: 'numeric',
                    minute: '2-digit',
                    meridiem: 'short'
                  }}
                  headerToolbar={{
                    left: 'prev,next today',
                    center: 'title',
                    right: 'dayGridMonth,timeGridWeek,listWeek'
                  }}
                  events={calendarEvents}
                  editable={true}
                  droppable={true}
                  dateClick={handleDateClick}
                  eventDrop={handleEventDrop}
                  height="auto"
                  eventClassNames={(arg) => {
                    // Return category-based class names
                    const category = arg.event.extendedProps.category || 'general';
                    return [`calendar-event-${category}`];
                  }}
                  eventContent={(eventInfo) => {
                    const time = eventInfo.event.start ? 
                      new Date(eventInfo.event.start).toLocaleTimeString('en-US', { 
                        hour: 'numeric', 
                        minute: '2-digit', 
                        hour12: true 
                      }) : '';
                    
                    const category = eventInfo.event.extendedProps.category || 'general';
                    
                    // Get icon based on category
                    let iconClass = 'calendar-event-icon-general';
                    let iconLetter = 'T';
                    if (category === 'crm') {
                      iconClass = 'calendar-event-icon-crm';
                      iconLetter = 'C';
                    } else if (category === 'followup') {
                      iconClass = 'calendar-event-icon-followup';
                      iconLetter = 'F';
                    } else if (category === 'task') {
                      iconClass = 'calendar-event-icon-task';
                      iconLetter = 'T';
                    }
                    
                    return (
                      <div className="calendar-event-modern">
                        <div className={`calendar-event-icon ${iconClass}`}>
                          {iconLetter}
                        </div>
                        <div className="calendar-event-content">
                          <div className="calendar-event-title-modern">
                            {eventInfo.event.title} {time && `• ${time}`}
                          </div>
                        </div>
                        {eventInfo.event.extendedProps.completed && (
                          <CheckCircle className="calendar-event-check-modern" />
                        )}
                      </div>
                    );
                  }}
                  eventMouseEnter={(info) => {
                    const rect = info.el.getBoundingClientRect();
                    setTooltip({
                      show: true,
                      x: rect.left + rect.width / 2,
                      y: rect.top - 10,
                      task: {
                        title: info.event.title,
                        description: info.event.extendedProps.description || 'No description',
                        priority: info.event.extendedProps.priority,
                        entity_type: info.event.extendedProps.entity_type,
                        completed: info.event.extendedProps.completed,
                        due_at: info.event.start
                      }
                    });
                  }}
                  eventMouseLeave={() => {
                    setTooltip({ show: false, x: 0, y: 0, task: null });
                  }}
                />
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Upcoming Section */}
            <Card className="shadow-md">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">Upcoming</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {pendingTasks
                  .filter(task => task.due_at && new Date(task.due_at) > new Date())
                  .sort((a, b) => new Date(a.due_at) - new Date(b.due_at))
                  .slice(0, 5)
                  .map(task => {
                    const category = task.entity_type === 'Lead' || task.entity_type === 'Contact' || task.entity_type === 'Company' 
                      ? 'crm' 
                      : task.title?.toLowerCase().includes('follow') 
                        ? 'followup' 
                        : 'task';
                    
                    let iconClass = 'sidebar-icon-general';
                    let iconLetter = 'T';
                    if (category === 'crm') {
                      iconClass = 'sidebar-icon-crm';
                      iconLetter = 'C';
                    } else if (category === 'followup') {
                      iconClass = 'sidebar-icon-followup';
                      iconLetter = 'F';
                    } else if (category === 'task') {
                      iconClass = 'sidebar-icon-task';
                      iconLetter = 'T';
                    }

                    const dueDate = new Date(task.due_at);
                    const timeStr = dueDate.toLocaleTimeString('en-US', { 
                      hour: 'numeric', 
                      minute: '2-digit', 
                      hour12: true 
                    });
                    const dateStr = dueDate.toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric' 
                    });

                    return (
                      <div 
                        key={task.id} 
                        className="sidebar-event-item"
                        onClick={() => handleEdit(task)}
                      >
                        <div className={`sidebar-event-icon ${iconClass}`}>
                          {iconLetter}
                        </div>
                        <div className="sidebar-event-content">
                          <div className="sidebar-event-title">{task.title}</div>
                          <div className="sidebar-event-meta">
                            {dateStr} • {timeStr}
                          </div>
                        </div>
                        <div className="sidebar-event-arrow">
                          <svg className="h-5 w-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                    );
                  })}
                {pendingTasks.filter(task => task.due_at && new Date(task.due_at) > new Date()).length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    <CalendarIcon className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                    <p className="text-sm">No upcoming tasks</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Today's Tasks Section */}
            <Card className="shadow-md">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">Today's Tasks</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {pendingTasks
                  .filter(task => {
                    if (!task.due_at) return false;
                    const taskDate = new Date(task.due_at).toDateString();
                    const today = new Date().toDateString();
                    return taskDate === today;
                  })
                  .slice(0, 4)
                  .map(task => (
                    <div 
                      key={task.id} 
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => handleEdit(task)}
                    >
                      <Circle className="h-4 w-4 text-slate-400" />
                      <span className="text-sm flex-1">{task.title}</span>
                    </div>
                  ))}
                
                {/* Quick Add Buttons */}
                <div className="pt-3 border-t space-y-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full justify-start text-slate-600 hover:bg-slate-50"
                    onClick={() => { resetForm(); setDialogOpen(true); }}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Task
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full justify-start text-slate-600 hover:bg-slate-50"
                    onClick={() => { 
                      resetForm(); 
                      setFormData(prev => ({ ...prev, title: 'Follow-up: ' }));
                      setDialogOpen(true); 
                    }}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Follow-up
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full justify-start text-slate-600 hover:bg-slate-50"
                    onClick={() => { 
                      resetForm(); 
                      setFormData(prev => ({ ...prev, title: 'Meeting: ' }));
                      setDialogOpen(true); 
                    }}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Meeting
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <>
          <div className="flex gap-2">
            <Button variant={filter === 'pending' ? 'default' : 'outline'} onClick={() => handleFilterChange('pending')}>Pending</Button>
            <Button variant={filter === 'completed' ? 'default' : 'outline'} onClick={() => handleFilterChange('completed')}>Completed</Button>
            <Button variant={filter === 'all' ? 'default' : 'outline'} onClick={() => handleFilterChange('all')}>All</Button>
          </div>

          <Card>
            <CardContent className="pt-6">
              {pendingTasks.length === 0 && filter === 'pending' ? (
                <div className="text-center py-12 text-slate-500">
                  <Circle className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                  <p>No pending tasks</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {(filter === 'pending' ? pendingTasks : filter === 'completed' ? completedTasks : [...pendingTasks, ...completedTasks]).map((task) => {
                    const isOverdue = !task.completed_at && task.due_at && new Date(task.due_at) < new Date();
                    return (
                      <div key={task.id} className={`flex items-start gap-4 p-4 rounded-lg border ${
                        task.completed_at ? 'bg-slate-50 border-slate-200' : 'bg-white border-slate-200'
                      } ${isOverdue ? 'border-red-200 bg-red-50' : ''}`}>
                        <button onClick={() => !task.completed_at && handleCompleteTask(task.id)} className="mt-1">
                          {task.completed_at ? (
                            <CheckCircle className="h-5 w-5 text-green-600" />
                          ) : (
                            <Circle className="h-5 w-5 text-slate-400 hover:text-blue-600" />
                          )}
                        </button>
                        <div className="flex-1">
                          <h4 className={`font-medium ${task.completed_at ? 'line-through text-slate-500' : 'text-slate-900'}`}>
                            {task.title}
                          </h4>
                          {task.description && (
                            <p className="text-sm text-slate-600 mt-1">{task.description}</p>
                          )}
                          <div className="flex items-center gap-3 mt-2">
                            <Badge className={getPriorityColor(task.priority)}>{task.priority}</Badge>
                            <Badge variant="outline" className="text-xs">{task.entity_type}</Badge>
                            {task.due_at && (
                              <div className={`flex items-center gap-1 text-xs ${
                                isOverdue ? 'text-red-600 font-medium' : 'text-slate-500'
                              }`}>
                                <CalendarIcon className="h-3 w-3" />
                                {formatDate(task.due_at)}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(task)}
                            className="text-blue-600 hover:text-blue-700"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(task)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Deleted View */}
      {viewMode === 'deleted' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-600" />
              Deleted Tasks
            </CardTitle>
          </CardHeader>
          <CardContent>
            {deletedTasks.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Trash2 className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <p>No deleted tasks</p>
              </div>
            ) : (
              <div className="space-y-3">
                {deletedTasks.map((task) => (
                  <div key={task.id} className="flex items-start gap-4 p-4 rounded-lg border border-dashed bg-slate-50 opacity-75">
                    <Circle className="h-5 w-5 text-slate-400 mt-1" />
                    <div className="flex-1">
                      <h4 className="font-medium text-slate-700">{task.title}</h4>
                      {task.description && (
                        <p className="text-sm text-slate-600 mt-1">{task.description}</p>
                      )}
                      <div className="flex items-center gap-3 mt-2">
                        <Badge className={getPriorityColor(task.priority)}>{task.priority}</Badge>
                        <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">Deleted</Badge>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRestore(task)}
                      className="text-green-600 hover:text-green-700"
                    >
                      <RotateCcw className="h-4 w-4 mr-1" />
                      Restore
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Task Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingTask ? 'Edit Task' : 'Add New Task'}</DialogTitle>
            <DialogDescription>
              {editingTask ? 'Update task details' : 'Create a new task to track'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Task Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                placeholder="Enter task title"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Add task details..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="entity_type">Related To *</Label>
                <Select
                  value={formData.entity_type}
                  onValueChange={(value) => setFormData({ ...formData, entity_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="General">General Task</SelectItem>
                    <SelectItem value="Lead">Lead</SelectItem>
                    <SelectItem value="Project">Project</SelectItem>
                    <SelectItem value="Invoice">Invoice</SelectItem>
                    <SelectItem value="Company">Company</SelectItem>
                    <SelectItem value="Contact">Contact</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  Select 'General Task' for standalone tasks not linked to any entity
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="entity_id">Entity ID {formData.entity_type !== 'General' && '*'}</Label>
                <Input
                  id="entity_id"
                  value={formData.entity_id}
                  onChange={(e) => setFormData({ ...formData, entity_id: e.target.value })}
                  required={formData.entity_type !== 'General'}
                  disabled={formData.entity_type === 'General'}
                  placeholder={formData.entity_type === 'General' ? 'N/A for general tasks' : 'Enter entity ID'}
                  className={formData.entity_type === 'General' ? 'bg-slate-100' : ''}
                />
                <p className="text-xs text-slate-500 mt-1">
                  {formData.entity_type === 'General' 
                    ? 'Not required for general tasks' 
                    : `ID of the ${formData.entity_type} this task relates to`}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="priority">Priority *</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(value) => setFormData({ ...formData, priority: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Low">Low</SelectItem>
                    <SelectItem value="Med">Medium</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="due_at">Due Date</Label>
                <Input
                  id="due_at"
                  type="datetime-local"
                  value={formData.due_at}
                  onChange={(e) => setFormData({ ...formData, due_at: e.target.value })}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                {editingTask ? 'Update Task' : 'Create Task'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{taskToDelete?.title}"? 
              You can restore it later from the Deleted tab.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Tooltip */}
      {tooltip.show && tooltip.task && (
        <div
          className="calendar-tooltip"
          style={{
            left: `${tooltip.x}px`,
            top: `${tooltip.y}px`,
            transform: 'translateX(-50%) translateY(-100%)'
          }}
        >
          <div className="space-y-2">
            <div className="font-semibold text-slate-900 text-sm">{tooltip.task.title}</div>
            <div className="text-xs text-slate-600">{tooltip.task.description}</div>
            <div className="flex items-center gap-2 text-xs">
              <Badge className={getPriorityColor(tooltip.task.priority)} size="sm">
                {tooltip.task.priority}
              </Badge>
              <Badge variant="outline" size="sm">{tooltip.task.entity_type}</Badge>
              {tooltip.task.completed && (
                <Badge className="bg-green-100 text-green-800" size="sm">Completed</Badge>
              )}
            </div>
            {tooltip.task.due_at && (
              <div className="text-xs text-slate-500 flex items-center gap-1">
                <CalendarIcon className="h-3 w-3" />
                {new Date(tooltip.task.due_at).toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TasksPage;
